/// <amd-module name="ExtensionManagement/ManagementHub/ExtensionDetailPanel" />
import * as React from "react";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";

import { IVssPageContext } from "VSS/Platform/Context";
import { css } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { IButtonProps } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { tooltipString } from "VSSUI/Utilities/Date";
import { ZeroData } from "VSSUI/ZeroData";

import { ExtensionRequestState } from "./Contracts/ExtensionManagement";
import { ExtensionHistoryTable } from "./ExtensionHistoryTable";
import { IExtensionsService } from "./ExtensionsService";
import { PublisherNameComponent } from "./PublisherNameComponent";
import { ReauthorizeExtensionDialog } from "./ReauthorizeExtensionDialog";
import { RejectRequestDialog } from "./RejectRequestDialog";
import { RequestDetailsTable } from "./RequestDetailsTable";
import { IExtensionViewModel, IHistoryAction, IInstalledExtensionViewModel, IRequestedExtensionViewModel, TabType } from "./UIContracts";
import { UninstallExtensionDialog } from "./UninstallExtensionDialog";

import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

const enum OpenDialog {
    None,
    ReauthorizeExtensionDialog,
    UninstallExtensionDialog,
    RejectRequest
}

interface IExtensionDetailPanelProps {
    /**
     * Extensions service instance
     */
    extensionsService: IExtensionsService;

    /**
     * Context for Persona provider.
     */
    pageContext: IVssPageContext;

    /**
     * Initially selected extension
     */
    selectedExtension: IExtensionViewModel;

    /**
     * Page state history observable to update on selection
     */
    selectedExtensionIdentifier: ObservableValue<string>;

    /**
     * Currently selected tab
     */
    selectedTabId: string;

    /**
     * Delegate to switch to main view
     */
    switchToMainView: () => void;
}

interface IExtensionDetailPanelState {
    /**
     * History of current extension
     */
    history?: ObservableArray<IHistoryAction | ObservableValue<IHistoryAction | undefined>>;

    /**
     * Initially selected extension
     */
    selectedExtension: IExtensionViewModel;
}

/**
 * Displays details of extension for master details panel
 */
export class ExtensionDetailPanelContent extends React.Component<IExtensionDetailPanelProps, IExtensionDetailPanelState> {
    private currentDialog: ObservableValue<OpenDialog>;
    private newScopes: ObservableValue<string[]>;

    constructor(props: IExtensionDetailPanelProps) {
        super(props);

        this.currentDialog = new ObservableValue<OpenDialog>(OpenDialog.None);
        this.newScopes = new ObservableValue<string[]>([]);

        if (props.selectedTabId === TabType.installed) {
            this.state = {
                history: props.extensionsService.getExtensionHistory(props.selectedExtension),
                selectedExtension: props.selectedExtension,
            };
        } else {
            this.state = { selectedExtension: props.selectedExtension };
        }
    }

    public render(): JSX.Element {
        if (!this.props.selectedExtension) {
            return (
                <div className="flex-column flex-grow justify-center">
                    <ZeroData
                        imagePath={getAssetLocation(this.props.pageContext, "ms.vss-extmgmt-web/ext-management-hub/zerodata.png")}
                        imageAltText=""
                        primaryText={
                            this.props.selectedTabId === TabType.installed
                                ? Resources.InstalledExtensionNotFound
                                : this.props.selectedTabId === TabType.requested
                                ? Resources.RequestedExtensionNotFound
                                : Resources.SharedExtensionNotFound
                        }
                    />
                </div>
            );
        }

        const details: [{ label: string; value: React.ReactNode }] = [
            {
                label: Resources.Publisher,
                value: <PublisherNameComponent extension={this.state.selectedExtension} />
            }
        ];

        let header: JSX.Element | undefined = undefined;
        let messageCard: JSX.Element | undefined = undefined;
        let secondCard: JSX.Element | undefined = undefined;

        if (this.props.selectedTabId === TabType.installed) {
            let installedExtension: IInstalledExtensionViewModel = this.state.selectedExtension as IInstalledExtensionViewModel;

            header = (
                <Observer extensionDisabled={installedExtension.extensionDisabled}>
                    {(props: { extensionDisabled: boolean }) => {
                        return this.renderHeaderContent(props.extensionDisabled);
                    }}
                </Observer>
            );

            if (installedExtension.reauthorizationRequired) {
                const buttons: IButtonProps[] = [
                    {
                        text: Resources.Review,
                        onClick: this.onReviewClicked
                    }
                ];

                messageCard = (
                    <div className="page-content page-content-top">
                        <MessageCard severity={MessageCardSeverity.Info} buttonProps={buttons}>
                            {format(Resources.ExtensionRequestingAuthorization, installedExtension.extensionName)}
                        </MessageCard>
                    </div>
                );
            }

            this.props.extensionsService.getNewScopes(installedExtension).then((data: string[]) => {
                this.newScopes.value = data;
            });

            if (installedExtension.version === installedExtension.latestAvailableVersion) {
                details.push(
                    {
                        label: Resources.InstalledVersion,
                        value: <span data-testid="installed-version">{format("{0} ({1})", installedExtension.version, Resources.Latest)}</span>
                    },
                    {
                        label: Resources.LastUpdated,
                        value: tooltipString(this.state.selectedExtension.lastUpdated)
                    }
                );
            } else {
                details.push(
                    {
                        label: Resources.InstalledVersion,
                        value: this.state.selectedExtension.version
                    },
                    {
                        label: Resources.LatestVersion,
                        value: installedExtension.latestAvailableVersion
                    }
                );
            }

            secondCard = (
                <div className="page-content page-content-top">
                    <Observer history={this.state.history}>
                        {(props: { history: (IHistoryAction | ObservableValue<IHistoryAction | undefined>)[] | undefined }) => {
                            return props.history!.length === 0 ? (
                                <Card className="flex-noshrink" titleProps={{ text: Resources.History, size: TitleSize.Medium, ariaLevel: 3 }}>
                                    <span className="no-history-message flex-grow">{Resources.NoExtensionHistory}</span>
                                </Card>
                            ) : (
                                <Card
                                    className="flex-noshrink bolt-table-card"
                                    titleProps={{ text: Resources.History, size: TitleSize.Medium, ariaLevel: 3 }}
                                >
                                    <ExtensionHistoryTable history={props.history!} pageContext={this.props.pageContext} />
                                </Card>
                            );
                        }}
                    </Observer>
                </div>
            );
        } else if (this.props.selectedTabId === TabType.requested) {
            header = this.renderHeaderContent();
            secondCard = (
                <div className="page-content page-content-top">
                    <Card className="flex-grow bolt-table-card" titleProps={{ text: Resources.RequestDetailsHeading, size: TitleSize.Medium }}>
                        <RequestDetailsTable
                            extension={this.state.selectedExtension as IRequestedExtensionViewModel}
                            pageContext={this.props.pageContext}
                        />
                    </Card>
                </div>
            );
        } else if (this.props.selectedTabId === TabType.shared) {
            header = this.renderHeaderContent();
            details.push(
                {
                    label: Resources.Version,
                    value: this.state.selectedExtension.version
                },
                {
                    label: Resources.LastUpdated,
                    value: tooltipString(this.state.selectedExtension.lastUpdated)
                }
            );
        }

        return (
            <Page className="flex-grow extension-detail-panel">
                {header}
                {messageCard}
                <div className="page-content page-content-top">
                    <Card
                        className="flex-noshrink"
                        titleProps={{ text: Resources.ExtensionDetails, size: TitleSize.Medium, ariaLevel: 3 }}
                        headerDescriptionProps={{ text: this.state.selectedExtension.shortDescription }}
                    >

                        <div className="flex-row extension-info-columns flex-wrap">
                            {details.map((items, index) => (
                                <div key={index}>
                                    <div className="body-m secondary-text">{items.label}</div>
                                    <div className="body-m primary-text">{items.value}</div>
                                </div>
                            ))}

                            <Observer scopes={this.newScopes}>
                                {(props: {scopes: string[]}) => {
                                    return props.scopes?.length ? (
                                        <div className="body-m primary-text margin-top-16" style={{flexBasis: "100%"}}>
                                            <div className="body-m secondary-text">{Resources.Permissions}</div>
                                            <span data-testid="extension-scope">{this.getStringOfScopes(props.scopes)}</span>
                                        </div>
                                    ) : null;
                                }}
                            </Observer>
                        </div>

                    </Card>
                </div>
                {secondCard}
                <Observer openDialog={this.currentDialog} scopes={this.newScopes}>
                    {(props: { openDialog: OpenDialog, scopes: string[] }) => {
                        return props.openDialog === OpenDialog.ReauthorizeExtensionDialog ? (
                            <ReauthorizeExtensionDialog
                                onContinueReauthorize={this.onContinueReauthorize}
                                extension={this.state.selectedExtension}
                                onDismiss={this.onDialogDismiss}
                                userIsAdmin={this.props.extensionsService.getIsUserAdmin()}
                                extensionService={this.props.extensionsService}
                                newScopes={props.scopes}
                            />
                        ) : props.openDialog === OpenDialog.UninstallExtensionDialog ? (
                            <UninstallExtensionDialog
                                onDismiss={this.onDialogDismiss}
                                onContinueUninstall={this.onContinueUninstall}
                                extension={this.state.selectedExtension}
                            />
                        ) : props.openDialog === OpenDialog.RejectRequest ? (
                            <RejectRequestDialog
                                onDismiss={this.onDialogDismiss}
                                onContinueRejectRequest={this.onContinueRejectRequest}
                                extension={this.state.selectedExtension}
                            />
                        ) : null;
                    }}
                </Observer>
            </Page>
        );
    }

    componentDidUpdate() {
        this.props.selectedExtensionIdentifier.value = this.props.extensionsService.getExtensionIdentifier(this.state.selectedExtension);
    }

    static getDerivedStateFromProps(props: IExtensionDetailPanelProps, state: IExtensionDetailPanelState) {
        if (props.selectedTabId !== TabType.installed || state.selectedExtension === props.selectedExtension) {
            return { selectedExtension: props.selectedExtension };
        }
        return { history: props.extensionsService.getExtensionHistory(props.selectedExtension), selectedExtension: props.selectedExtension };
    }

    private getStringOfScopes(scopes: string[]): string {
        let scopesList = this.props.extensionsService.getFormattedScopes(scopes);
        return scopesList.join(", ");
    }

    private getCommandBarItems(): IHeaderCommandBarItem[] {
        let commandBarItems: IHeaderCommandBarItem[] = [];

        if (this.props.selectedTabId === TabType.installed) {
            let installedExtension: IInstalledExtensionViewModel = this.state.selectedExtension as IInstalledExtensionViewModel;

            if (this.props.extensionsService.getIsUserAdmin()) {
                commandBarItems.push(
                    {
                        id: "uninstall",
                        text: Resources.Uninstall,
                        onActivate: this.onUninstallClicked,
                        important: true
                    },
                    {
                        id: "enable-disable",
                        text: installedExtension.extensionDisabled.value ? Resources.Enable : Resources.Disable,
                        onActivate: this.onEnableDisableClicked,
                        important: false
                    }
                );
            }
        } else if (this.props.selectedTabId === TabType.requested) {
            let requestedExtension: IRequestedExtensionViewModel = this.state.selectedExtension as IRequestedExtensionViewModel;

            if (this.props.extensionsService.getIsUserAdmin() && requestedExtension.requestState === ExtensionRequestState.Open) {
                commandBarItems.push(
                    {
                        id: "approve",
                        text: Resources.Approve,
                        onActivate: this.onApproveRequestClicked
                    },
                    {
                        id: "reject",
                        text: Resources.Reject,
                        important: false,
                        onActivate: this.onRejectRequestClicked
                    }
                );
            } else if (!this.props.extensionsService.getIsUserAdmin()) {
                commandBarItems.push({
                    id: "delete",
                    text: Resources.Delete,
                    onActivate: this.onRequestDeleteClicked,
                    important: true
                });
            }
        } else {
            if (this.props.extensionsService.getIsUserAdmin()) {
                commandBarItems.push({
                    id: "install",
                    text: Resources.Install,
                    onActivate: this.onInstallClicked
                });
            }
        }

        commandBarItems.push({
            id: "marketplace",
            text: Resources.Marketplace,
            href: this.state.selectedExtension.extensionDetailsUrl,
            target: "_blank",
            important: true
        });

        return commandBarItems;
    }

    private renderHeaderContent = (extensionDisabled: boolean = false): JSX.Element => {
        const iconProps = {
            render: (className: string) => {
                return (
                    <img
                        alt=""
                        className={css(className, "extension-icon", extensionDisabled && "disabled-icon")}
                        src={this.state.selectedExtension.extensionIconUrl}
                    />
                );
            }
        };
        return (
            <Header
                className="details-content-header"
                titleIconProps={iconProps}
                title={this.state.selectedExtension.extensionName}
                titleAriaLevel={2}
                titleSize={TitleSize.Large}
                commandBarItems={this.getCommandBarItems()}
            />
        );
    };

    private onApproveRequestClicked = (): void => {
        this.props.extensionsService.installExtension(this.state.selectedExtension);
    };

    private onContinueReauthorize = (): void => {
        this.props.extensionsService.reauthorizeExtension(this.state.selectedExtension as IInstalledExtensionViewModel);
        this.currentDialog.value = OpenDialog.None;
        this.props.switchToMainView();
    };

    private onContinueRejectRequest = (reason: string): void => {
        this.props.extensionsService.rejectRequest(this.state.selectedExtension as IRequestedExtensionViewModel, reason);
        this.currentDialog.value = OpenDialog.None;
        this.props.switchToMainView();
    };

    private onContinueUninstall = (reason?: string, reasonCode?: string): void => {
        this.props.extensionsService.uninstallExtension(this.state.selectedExtension as IInstalledExtensionViewModel, reason, reasonCode);
        this.currentDialog.value = OpenDialog.None;
        this.props.switchToMainView();
    };

    private onDialogDismiss = (): void => {
        this.currentDialog.value = OpenDialog.None;
    };

    private onEnableDisableClicked = (): void => {
        let installedExtension = this.state.selectedExtension as IInstalledExtensionViewModel;
        if (installedExtension.extensionDisabled.value) {
            this.props.extensionsService.enableExtension(installedExtension);
        } else {
            this.props.extensionsService.disableExtension(installedExtension);
        }
    };

    private onInstallClicked = (): void => {
        this.props.extensionsService.installExtension(this.state.selectedExtension);
    };

    private onRejectRequestClicked = (): void => {
        this.currentDialog.value = OpenDialog.RejectRequest;
    };

    private onRequestDeleteClicked = (): void => {
        this.props.extensionsService.deleteRequest(this.state.selectedExtension as IRequestedExtensionViewModel);
        this.props.switchToMainView();
    };

    private onReviewClicked = (): void => {
        let installedExtension = this.state.selectedExtension as IInstalledExtensionViewModel;
        this.props.extensionsService.getNewScopes(installedExtension).then((data: string[]) => {
            this.newScopes.value = data;
            this.currentDialog.value = OpenDialog.ReauthorizeExtensionDialog;
        });
    };

    private onUninstallClicked = (): void => {
        this.currentDialog.value = OpenDialog.UninstallExtensionDialog;
    };
}
